<script setup>
/* global axios */
// 3º box de "Fluxos de Conversa" — exige um evento de conversão (Meta CAPI) antes
// de resolver o ticket. Aparece SÓ quando a conta configurou o Meta CAPI. A config
// mora na tabela meta_capi_configs (aditivo). Espelha o box de atributos obrigatórios.
import { ref, computed, onMounted } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/meta_capi_config`;

const inboxes = useMapGetter('inboxes/getInboxes');

// mesma lista do box de conversão do ticket
const EVENTS = [
  'Lead',
  'Contact',
  'Schedule',
  'CompleteRegistration',
  'SubmitApplication',
  'ViewContent',
  'AddToCart',
  'InitiateCheckout',
  'StartTrial',
  'Subscribe',
  'Purchase',
];
const EVENT_KEY = {
  Lead: 'LEAD',
  Contact: 'CONTACT',
  Schedule: 'SCHEDULE',
  CompleteRegistration: 'COMPLETE_REGISTRATION',
  SubmitApplication: 'SUBMIT_APPLICATION',
  ViewContent: 'VIEW_CONTENT',
  AddToCart: 'ADD_TO_CART',
  InitiateCheckout: 'INITIATE_CHECKOUT',
  StartTrial: 'START_TRIAL',
  Subscribe: 'SUBSCRIBE',
  Purchase: 'PURCHASE',
};

const loading = ref(true);
const configured = ref(false);
const required = ref(false);
const selected = ref([]);
const selectedInboxIds = ref([]);
const saving = ref(false);

const label = name => t(`META_CAPI.EVENTS.${EVENT_KEY[name]}`);
const isChecked = name => selected.value.includes(name);
// vazio = "Todos" (qualquer evento libera)
const allEvents = computed(() => selected.value.length === 0);

// caixas da conta (id + nome)
const accountInboxes = computed(() =>
  (inboxes.value || []).map(i => ({ id: i.id, name: i.name }))
);
const [showInboxDropdown, toggleInboxDropdown] = useToggle(false);
const closeInboxDropdown = () => toggleInboxDropdown(false);

// opções do dropdown = caixas ainda NÃO adicionadas
const availableInboxOptions = computed(() => {
  const set = new Set(selectedInboxIds.value);
  return accountInboxes.value
    .filter(i => !set.has(i.id))
    .map(i => ({ label: i.name, value: i.id, action: 'add' }));
});
// caixas adicionadas (na ordem da conta)
const selectedInboxObjs = computed(() =>
  accountInboxes.value.filter(i => selectedInboxIds.value.includes(i.id))
);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(base);
    const d = data.data || {};
    configured.value = !!d.configured;
    required.value = !!d.require_event_on_resolve;
    selected.value = Array.isArray(d.required_events) ? d.required_events : [];
    selectedInboxIds.value = Array.isArray(d.required_inbox_ids)
      ? d.required_inbox_ids
      : [];
  } catch (e) {
    configured.value = false;
  } finally {
    loading.value = false;
  }
};

const persist = async () => {
  saving.value = true;
  try {
    await axios.patch(base, {
      meta_capi_config: {
        require_event_on_resolve: required.value,
        required_events: selected.value,
        required_inbox_ids: selectedInboxIds.value,
      },
    });
    useAlert(t('META_CAPI.RESOLVE_REQUIREMENT.SAVE.SUCCESS'));
  } catch (e) {
    useAlert(t('META_CAPI.RESOLVE_REQUIREMENT.SAVE.ERROR'));
    await load(); // reverte pro estado do servidor
  } finally {
    saving.value = false;
  }
};

// o Switch (v-model) já atualiza `required`; aqui só persiste
const onRequiredChange = async () => {
  await persist();
};

// "Todos" = qualquer evento libera (lista vazia)
const selectAllEvents = async () => {
  if (allEvents.value) return;
  selected.value = [];
  await persist();
};

const toggleEvent = async name => {
  if (isChecked(name)) {
    selected.value = selected.value.filter(n => n !== name);
  } else {
    selected.value = [...selected.value, name];
  }
  await persist();
};

const onAddInbox = async ({ value }) => {
  if (value == null || saving.value) return;
  if (!selectedInboxIds.value.includes(value)) {
    selectedInboxIds.value = [...selectedInboxIds.value, value];
    await persist();
  }
  closeInboxDropdown();
};

const removeInbox = async id => {
  if (saving.value) return;
  selectedInboxIds.value = selectedInboxIds.value.filter(x => x !== id);
  await persist();
};

const show = computed(() => configured.value);

onMounted(() => {
  // garante que a lista de caixas esteja carregada mesmo abrindo direto no settings
  if (!accountInboxes.value.length) store.dispatch('inboxes/get');
  load();
});
</script>

<template>
  <div
    v-if="!loading && show"
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <div class="flex justify-between items-center w-full gap-4">
        <div class="flex flex-col gap-2">
          <h3 class="text-heading-2 text-n-slate-12">
            {{ t('META_CAPI.RESOLVE_REQUIREMENT.TITLE') }}
          </h3>
          <p class="mb-0 text-body-para text-n-slate-11">
            {{ t('META_CAPI.RESOLVE_REQUIREMENT.DESCRIPTION') }}
          </p>
        </div>
        <Switch
          v-model="required"
          :disabled="saving"
          @change="onRequiredChange"
        />
      </div>
    </div>

    <div v-if="required" class="flex flex-col gap-3 px-5 py-4">
      <!-- eventos que liberam o resolve -->
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('META_CAPI.RESOLVE_REQUIREMENT.EVENTS_LABEL') }}
      </p>
      <p class="-mt-2 text-xs text-n-slate-10">
        {{ t('META_CAPI.RESOLVE_REQUIREMENT.EVENTS_HINT') }}
      </p>
      <div class="flex flex-wrap gap-2">
        <button
          type="button"
          :disabled="saving"
          class="inline-flex items-center gap-1.5 px-3 h-8 text-xs font-medium border rounded-lg transition-colors disabled:opacity-60"
          :class="
            allEvents
              ? 'bg-n-brand/15 border-n-brand/50 text-n-brand'
              : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="selectAllEvents"
        >
          <span
            class="size-3.5 shrink-0"
            :class="allEvents ? 'i-lucide-check' : 'i-lucide-asterisk'"
          />
          {{ t('META_CAPI.RESOLVE_REQUIREMENT.ALL_EVENTS') }}
        </button>
        <button
          v-for="name in EVENTS"
          :key="name"
          type="button"
          :disabled="saving"
          class="inline-flex items-center gap-1.5 px-3 h-8 text-xs font-medium border rounded-lg transition-colors disabled:opacity-60"
          :class="
            isChecked(name)
              ? 'bg-n-brand/15 border-n-brand/50 text-n-brand'
              : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="toggleEvent(name)"
        >
          <span
            class="size-3.5 shrink-0"
            :class="isChecked(name) ? 'i-lucide-check' : 'i-lucide-plus'"
          />
          {{ label(name) }}
        </button>
      </div>

      <!-- vincular por caixa (dropdown + lista, igual atributos obrigatórios) -->
      <div class="flex items-center justify-between gap-4 mt-2">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('META_CAPI.RESOLVE_REQUIREMENT.INBOX_LABEL') }}
        </p>
        <div v-on-clickaway="closeInboxDropdown" class="relative">
          <Button
            icon="i-lucide-circle-plus"
            :label="t('META_CAPI.RESOLVE_REQUIREMENT.ADD_INBOX')"
            size="sm"
            :is-loading="saving"
            :disabled="saving || availableInboxOptions.length === 0"
            @click="toggleInboxDropdown()"
          />
          <DropdownMenu
            v-if="showInboxDropdown"
            :menu-items="availableInboxOptions"
            show-search
            :search-placeholder="
              t('META_CAPI.RESOLVE_REQUIREMENT.ADD_INBOX_SEARCH')
            "
            class="top-full mt-1 w-56 ltr:right-0 rtl:left-0"
            @action="onAddInbox"
          />
        </div>
      </div>
      <p class="-mt-1 text-xs text-n-slate-10">
        {{ t('META_CAPI.RESOLVE_REQUIREMENT.INBOX_HINT') }}
      </p>

      <div
        v-if="selectedInboxObjs.length === 0"
        class="flex items-center gap-2 px-3 py-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-10"
      >
        <span class="i-lucide-inbox size-3.5 shrink-0" />
        {{ t('META_CAPI.RESOLVE_REQUIREMENT.ALL_INBOXES_EMPTY') }}
      </div>
      <div v-else class="flex flex-col gap-1.5">
        <div
          v-for="inbox in selectedInboxObjs"
          :key="inbox.id"
          class="flex items-center justify-between gap-2 px-3 py-2 border rounded-lg border-n-weak"
        >
          <span class="flex items-center gap-2 min-w-0 text-sm text-n-slate-12">
            <span class="i-lucide-inbox size-4 shrink-0 text-n-slate-11" />
            <span class="truncate">{{ inbox.name }}</span>
          </span>
          <button
            type="button"
            :disabled="saving"
            class="grid rounded-md size-7 place-items-center text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-2 disabled:opacity-60"
            @click="removeInbox(inbox.id)"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
